// Ping wheel — radial menu for quick team comms. Per the plan, V holds
// a directional fan. For scope the current implementation uses
// TAP-V-to-open / click-or-1234-to-fire, which is functionally the same
// and avoids the held-input cancel-on-release edge cases.

export type PingCmd = 'follow' | 'help' | 'wait' | 'danger';

type PingOption = {
  cmd: PingCmd;
  label: string;
  icon: string;
  color: string;
  angle: number;
};

const OPTIONS: PingOption[] = [
  { cmd: 'follow', label: 'Follow Me', icon: '⬆', color: '#22c55e', angle:   0 },
  { cmd: 'help',   label: 'Help!',     icon: '➡', color: '#f59e0b', angle:  90 },
  { cmd: 'wait',   label: 'Wait',      icon: '⬇', color: '#60a5fa', angle: 180 },
  { cmd: 'danger', label: 'Danger!',   icon: '⬅', color: '#ef4444', angle: 270 },
];

export const PING_LABELS: Record<PingCmd, string> = {
  follow: '⬆ FOLLOW ME',
  help:   '➡ HELP!',
  wait:   '⬇ WAIT',
  danger: '⬅ DANGER!',
};

export class PingWheel {
  private el: HTMLDivElement;
  private open = false;
  private onPick: (cmd: PingCmd) => void = () => {};

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'ui';
    this.el.style.cssText = `
      position: fixed;
      left: 50%; top: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      display: none;
      width: 360px; height: 360px;
      z-index: 120;
      font: 800 13px system-ui, sans-serif;
      color: #e5e7eb;
    `;
    parent.appendChild(this.el);
    addEventListener('keydown', (e) => {
      if (!this.open) return;
      if (e.code === 'Escape' || e.code === 'KeyV') { this.hide(); return; }
      if (e.code === 'Digit1' || e.code === 'Numpad1') { this.fire('follow'); return; }
      if (e.code === 'Digit2' || e.code === 'Numpad2') { this.fire('help');   return; }
      if (e.code === 'Digit3' || e.code === 'Numpad3') { this.fire('wait');   return; }
      if (e.code === 'Digit4' || e.code === 'Numpad4') { this.fire('danger'); return; }
    });
  }

  setOnPick(cb: (cmd: PingCmd) => void) {
    this.onPick = cb;
  }

  toggle() {
    if (this.open) this.hide();
    else this.show();
  }

  show() {
    if (this.open) return;
    this.open = true;
    this.el.style.display = 'block';
    this.el.innerHTML = '';

    const center = document.createElement('div');
    center.style.cssText = `
      position: absolute; left: 50%; top: 50%;
      transform: translate(-50%,-50%);
      width: 88px; height: 88px;
      border-radius: 50%;
      background: rgba(15,23,42,.88);
      border: 2px solid #475569;
      display: flex; align-items: center; justify-content: center;
      font: 800 11px system-ui; letter-spacing: 1.5px;
      color: #94a3b8;
      box-shadow: 0 10px 30px rgba(0,0,0,.6);
    `;
    center.textContent = 'PING';
    this.el.appendChild(center);

    for (let i = 0; i < OPTIONS.length; i++) {
      const opt = OPTIONS[i];
      const rad = (opt.angle - 90) * Math.PI / 180;
      const radius = 120;
      const cx = 180 + Math.cos(rad) * radius;
      const cy = 180 + Math.sin(rad) * radius;
      const btn = document.createElement('div');
      btn.style.cssText = `
        position: absolute;
        left: ${cx}px; top: ${cy}px;
        transform: translate(-50%,-50%);
        width: 118px; padding: 12px 8px;
        border-radius: 12px;
        background: ${opt.color}22;
        border: 2px solid ${opt.color};
        text-align: center;
        pointer-events: auto;
        cursor: pointer;
        transition: transform .12s, background .12s;
      `;
      btn.innerHTML = `
        <div style="font-size:24px;color:${opt.color};line-height:1;">${opt.icon}</div>
        <div style="margin-top:6px;color:${opt.color};">${opt.label}</div>
        <div style="font-size:9px;opacity:.65;margin-top:2px;">[${i + 1}]</div>
      `;
      btn.addEventListener('mouseenter', () => {
        btn.style.background = `${opt.color}44`;
        btn.style.transform = 'translate(-50%,-50%) scale(1.07)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = `${opt.color}22`;
        btn.style.transform = 'translate(-50%,-50%) scale(1)';
      });
      btn.addEventListener('click', () => this.fire(opt.cmd));
      this.el.appendChild(btn);
    }
  }

  hide() {
    this.open = false;
    this.el.style.display = 'none';
  }

  isOpen(): boolean {
    return this.open;
  }

  private fire(cmd: PingCmd) {
    this.onPick(cmd);
    this.hide();
  }
}
